import {
  createContext, ReactNode, useContext, useMemo, useState,
} from 'react';
import { DocumentSearchDetailForm } from '../services/http/documents.api';

export type ContractType = 'ELECTRONIC' | 'PAPER' | 'ALL';

type SearchBarContextType = {
  isSearching: boolean,
  isAttachingDocumentSearch: boolean,
  text: string | undefined,
  temporaryText: string | undefined,
  warehouseStatus: boolean,
  contractType: ContractType,
  inUse: boolean,
  advancedSearch?: DocumentSearchDetailForm,
  advancedSearchForPagination?: DocumentSearchDetailForm,
  setIsSearching: (isSearching: boolean) => void,
  setIsAttachingDocumentSearch: (isAttachingDocumentSearch: boolean) => void,
  setText: (search: string | undefined) => void,
  setTemporaryText: (search: string | undefined) => void,
  setAdvancedSearch: (search: DocumentSearchDetailForm | undefined) => void,
  setAdvancedSearchForPagination: (search: DocumentSearchDetailForm | undefined) => void,
  toggleContractType: () => void,
  toggleWarehouseStatus: () => void,
  enable: () => void,
  disable: () => void,
};

export const SearchBarContext = createContext<SearchBarContextType>(
  {
    isSearching: false,
    isAttachingDocumentSearch: false,
    contractType: 'ALL',
    warehouseStatus: false,
    text: '',
    temporaryText: '',
    inUse: false,
    setIsSearching: () => {},
    setIsAttachingDocumentSearch: () => {},
    setText: () => ({}),
    setTemporaryText: () => ({}),
    setAdvancedSearch: () => ({}),
    setAdvancedSearchForPagination: () => ({}),
    toggleWarehouseStatus: () => ({}),
    toggleContractType: () => ({}),
    enable: () => ({}),
    disable: () => ({}),
  },
);

export default function SearchBarProvider({ children }: { children: ReactNode[] | ReactNode }) {
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isAttachingDocumentSearch, setIsAttachingDocumentSearch] = useState<boolean>(false);
  const [text, setText] = useState<string | undefined>('');
  const [temporaryText, setTemporaryText] = useState<string | undefined>('');
  const [advancedSearch, setAdvancedSearch] = useState<DocumentSearchDetailForm | undefined>(undefined);
  const [advancedSearchForPagination, setAdvancedSearchForPagination] = useState<DocumentSearchDetailForm | undefined>(undefined);
  const [contractType, setContractType] = useState<ContractType>('ALL');
  const [warehouseStatus, setWarehouseStatus] = useState<boolean>(false);
  const [uses, setUses] = useState<number>(0);
  const value = useMemo(() => ({
    isSearching,
    isAttachingDocumentSearch,
    text,
    temporaryText,
    inUse: !!uses,
    contractType,
    warehouseStatus,
    advancedSearch,
    advancedSearchForPagination,
    setIsSearching,
    setIsAttachingDocumentSearch,
    setText,
    setTemporaryText,
    setAdvancedSearch,
    setAdvancedSearchForPagination,
    toggleContractType: () => {
      if (contractType === 'ELECTRONIC') {
        setContractType('PAPER');
      } else if (contractType === 'PAPER') {
        setContractType('ALL');
      } else {
        setContractType('ELECTRONIC');
      }
    },
    toggleWarehouseStatus: () => setWarehouseStatus(!warehouseStatus),
    enable() {
      setUses(uses + 1);
    },
    disable() {
      setUses(Math.max(0, uses - 1));
    },
  }), [temporaryText, text, advancedSearch, advancedSearchForPagination, contractType, warehouseStatus, uses, isSearching]);

  return (
    <SearchBarContext.Provider value={value}>
      {children}
    </SearchBarContext.Provider>
  );
}

export const useSearchBarContext = () => useContext(SearchBarContext);
